import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../utils/axiosConfig';

// Types
export interface BudgetScenario {
  id: string;
  name: string;
  description: string;
  threshold: {
    type: 'task_count' | 'completion_percentage' | 'revenue_target';
    value: number;
    operator: 'gte' | 'lte' | 'eq';
  };
  budgetDistribution: {
    teamExecutive: number;
    teamLeader: number;
    districtHead: number;
    stateHead: number;
    regionalHead: number;
    admin: number;
  };
  isActive: boolean;
}

export interface Task {
  _id: string;
  title: string;
  description: string;
  taskUrl: string;
  company: {
    _id: string;
    companyName: string;
    logo?: string;
  };
  postedBy: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  totalBudget: number;
  budgetDistribution: {
    teamExecutive: number;
    teamLeader: number;
    districtHead: number;
    stateHead: number;
    regionalHead: number;
    admin: number;
  };
  budgetScenarios?: BudgetScenario[];
  currentScenario?: string | null;
  taskCompletionMetrics?: {
    totalTasksAssigned: number;
    completedTasks: number;
    completionPercentage: number;
    revenueGenerated: number;
    lastUpdated: string;
  };
  regions: string[];
  states: string[];
  districts: string[];
  targetDesignations: string[];
  requiredSkills: Array<{
    _id: string;
    name: string;
  }>;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  startDate: string;
  endDate: string;
  dataCollectionCriteria: string;
  validationRules: {
    requiredFields: string[];
  };
  assignedUsers: Array<{
    user: {
      _id: string;
      firstName: string;
      lastName: string;
      email: string;
      designation: string;
      region: string;
    };
    assignedAt: string;
    status: 'assigned' | 'accepted' | 'rejected' | 'completed';
    referralId?: string;
    referralLink?: string;
    myReferralLink?: string;
    dataSubmitted: Array<{
      studentName: string;
      studentEmail: string;
      studentPhone: string;
      studentDetails: any;
      submittedAt: string;
      isValidated: boolean;
      validationScore: number;
      validatedBy?: {
        _id: string;
        firstName: string;
        lastName: string;
      };
      validatedAt?: string;
    }>;
    earnings: {
      totalEarned: number;
      validatedEarnings: number;
      pendingEarnings: number;
    };
  }>;
  statistics: {
    totalDataSubmitted: number;
    validatedData: number;
    pendingValidation: number;
    totalEarningsDistributed: number;
    completionRate: number;
  };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TaskState {
  tasks: Task[];
  userTasks: Task[];
  currentTask: Task | null;
  isLoading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

const initialState: TaskState = {
  tasks: [],
  userTasks: [],
  currentTask: null,
  isLoading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  },
};

// Async thunks
export const updateTask = createAsyncThunk(
  'tasks/updateTask',
  async ({ taskId, taskData }: { taskId: string; taskData: any }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/tasks/${taskId}`, taskData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update task');
    }
  }
);

export const createTask = createAsyncThunk(
  'tasks/createTask',
  async (taskData: {
    title: string;
    description: string;
    taskUrl: string;
    totalBudget: number;
    budgetDistribution: {
      teamExecutive: number;
      teamLeader: number;
      districtHead: number;
      stateHead: number;
      regionalHead: number;
      admin: number;
    };
    regions: string[];
    states?: string[];
    districts?: string[];
    targetDesignations: string[];
    requiredSkills: string[];
    startDate: string;
    endDate: string;
    dataCollectionCriteria: string;
    validationRules: {
      requiredFields: string[];
    };
  }, { rejectWithValue }) => {
    try {
      const response = await api.post('/tasks', taskData);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Task creation failed');
    }
  }
);

export const getTasks = createAsyncThunk(
  'tasks/getTasks',
  async (params: {
    region?: string;
    state?: string;
    district?: string;
    designation?: string;
    status?: string;
    company?: string;
    page?: number;
    limit?: number;
  } = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/tasks', { params });
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch tasks');
    }
  }
);

export const getUserTasks = createAsyncThunk(
  'tasks/getUserTasks',
  async (params: {
    status?: string;
    page?: number;
    limit?: number;
  } = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/tasks/user-tasks', { params });
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user tasks');
    }
  }
);

export const getTaskById = createAsyncThunk(
  'tasks/getTaskById',
  async (taskId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/tasks/${taskId}`);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch task');
    }
  }
);

export const assignUserToTask = createAsyncThunk(
  'tasks/assignUserToTask',
  async ({ taskId, userId }: { taskId: string; userId: string }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/tasks/${taskId}/assign`, { userId });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to assign user to task');
    }
  }
);

export const submitTaskData = createAsyncThunk(
  'tasks/submitTaskData',
  async ({ taskId, studentData }: { taskId: string; studentData: any }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/tasks/${taskId}/submit-data`, { studentData });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to submit task data');
    }
  }
);

export const validateTaskData = createAsyncThunk(
  'tasks/validateTaskData',
  async ({ taskId, userId, dataIndex, validationScore }: {
    taskId: string;
    userId: string;
    dataIndex: number;
    validationScore: number;
  }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/tasks/${taskId}/validate-data`, {
        userId,
        dataIndex,
        validationScore
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to validate task data');
    }
  }
);

export const respondToTaskAssignment = createAsyncThunk(
  'tasks/respondToTaskAssignment',
  async ({ taskId, response }: { taskId: string; response: 'accepted' | 'rejected' }, { rejectWithValue }) => {
    try {
      const apiResponse = await api.put(`/tasks/${taskId}/respond`, { response });
      return apiResponse.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to respond to task assignment');
    }
  }
);

export const getTaskHierarchy = createAsyncThunk(
  'tasks/getTaskHierarchy',
  async (taskId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/tasks/hierarchy/${taskId}`);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch task hierarchy');
    }
  }
);

const taskSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentTask: (state, action: PayloadAction<Task | null>) => {
      state.currentTask = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Update Task
      .addCase(updateTask.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateTask.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.tasks.findIndex(task => task._id === action.payload._id);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(updateTask.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create Task
      .addCase(createTask.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createTask.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tasks.unshift(action.payload);
        state.error = null;
      })
      .addCase(createTask.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Get Tasks
      .addCase(getTasks.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getTasks.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tasks = action.payload.tasks;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getTasks.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Get User Tasks
      .addCase(getUserTasks.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUserTasks.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userTasks = action.payload.tasks;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getUserTasks.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Get Task By ID
      .addCase(getTaskById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getTaskById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentTask = action.payload;
        state.error = null;
      })
      .addCase(getTaskById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Assign User To Task
      .addCase(assignUserToTask.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(assignUserToTask.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(assignUserToTask.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Submit Task Data
      .addCase(submitTaskData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(submitTaskData.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(submitTaskData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Validate Task Data
      .addCase(validateTaskData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(validateTaskData.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(validateTaskData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Respond To Task Assignment
      .addCase(respondToTaskAssignment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(respondToTaskAssignment.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(respondToTaskAssignment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Get Task Hierarchy
      .addCase(getTaskHierarchy.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getTaskHierarchy.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(getTaskHierarchy.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setCurrentTask } = taskSlice.actions;
export default taskSlice.reducer;
